'use client';
/* eslint-disable tailwindcss/no-arbitrary-value */
import classNames from 'classnames';
import { forwardRef, memo, useEffect, useRef } from 'react';
import { ProductDetailsProps } from '../../ProductDetails';
import { PushNavInteraction } from './navTracking';
import {
  DEFAULT_STICKY_OFFSET,
  INavigationItem,
  STICKY_NAV_ACTIVE_BORDER,
  STICKY_NAV_ACTIVE_TEXT,
} from './StickyNavigationBar';
import { StickyNavigationBarApplyButton } from './StickyNavigationBarApplyButton';
import { scrollToSection, useActiveSection } from './useActiveSection';

type StickyNavigationBarDesktopProps = ProductDetailsProps & {
  navigationItems: INavigationItem[];
  SPACER_COUNT?: number;
  stickyOffset?: number;
  applyLabel?: string;
  itemListName?: string;
  /** The nav's own interaction event (default vs TI payload). */
  pushNavInteraction: PushNavInteraction;
  /**
   * Whether the bar carries the Apply CTA (and the fade strip that exists for it). Designer
   * decision (CMS-1924): only the TI provider page keeps it (CMS-1643); CC PDP surfaces — the full
   * page and the #usePDP modal alike — go without it: applying goes through the hero and the
   * page's sticky footer block.
   */
  showApplyCta?: boolean;
  /**
   * Whether the fade strip behind the Apply CTA carries the nav background. It must match that
   * background: the default nav is always backgrounded, the TI nav only once pinned.
   */
  fadeStripBackground?: boolean;
};

export const StickyNavigationBarDesktop: React.FC<
  StickyNavigationBarDesktopProps
> = (props) => {
  const {
    navigationItems,
    SPACER_COUNT,
    applyLabel,
    product,
    pushDataLayer,
    itemListName,
    pushNavInteraction,
    showApplyCta = false,
    fadeStripBackground = true,
  } = props;
  const stickyOffset = props.stickyOffset ?? DEFAULT_STICKY_OFFSET;
  const [selectedId, setSelectedManually] = useActiveSection(
    navigationItems.map((i) => i.id),
    stickyOffset,
  );

  // Trailing spacers keep the last real link clear of the fade strip.
  const spacers: INavigationItem[] = Array.from(
    { length: SPACER_COUNT ?? 3 },
    (_, i) => ({
      label: '',
      id: `_spacer_${i}`,
    }),
  );

  const items = [...navigationItems, ...spacers];

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // Keep the active nav button centered, whether the change came from a click
  // or from the user scrolling through sections.
  useEffect(() => {
    const container = scrollContainerRef.current;
    const el = itemRefs.current.get(selectedId);
    if (!container || !el) return;

    const containerRect = container.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const visibleWidth = container.clientWidth;
    const offset =
      elRect.left - containerRect.left - visibleWidth / 2 + elRect.width / 2;

    container.scrollTo({
      left: container.scrollLeft + offset,
      behavior: 'smooth',
    });
  }, [selectedId]);

  const handleClick = (item: INavigationItem) => {
    setSelectedManually(item.id);
    requestAnimationFrame(() => {
      scrollToSection(item.id, stickyOffset);
    });

    // Tab-navigation tracking. Spacers carry an empty label and are skipped.
    // The event payload is the nav's own `pushNavInteraction`, shared with the
    // mobile bar so both stay in parity. Index is the clicked tab's position
    // among the real nav items — spacers append after these, so their index
    // never leaks.
    if (item.label && pushDataLayer) {
      const index = navigationItems.findIndex(
        (navItem) => navItem.id === item.id,
      );
      pushNavInteraction({
        viewport: 'desktop',
        label: item.label,
        index,
        product,
        itemListName,
        pushDataLayer,
      });
    }
  };

  // The links scroll in a flex-1 column; the Apply CTA is a shrink-0 sibling
  // that sizes to its label (CMS-1991 QA: "Instant Checkout" overflowed the
  // former fixed 105px slot). The fade strip sits at the right edge of the
  // scroll column, so it always ends exactly where the button begins.
  return (
    <div className="flex w-full items-center gap-spacing/gap/x-sm">
      <div className="relative min-w-0 flex-1">
        <div
          ref={scrollContainerRef}
          className="overflow-x-auto scrollbar-none"
        >
          <div className="flex gap-10 snap-x">
            {items.map((item) => (
              <button
                type="button"
                key={item.id}
                onClick={() => handleClick(item)}
                role="tab"
                aria-selected={selectedId === item.id}
              >
                <NavigationItem
                  {...item}
                  isSelected={selectedId === item.id}
                  ref={(el) => {
                    if (el) {
                      itemRefs.current.set(item.id, el);
                    } else {
                      itemRefs.current.delete(item.id);
                    }
                  }}
                />
              </button>
            ))}
          </div>
        </div>

        {/* The fade strip exists for the nav CTA (see `showApplyCta`). */}
        {showApplyCta && (
          <div
            className={classNames(
              'pointer-events-none absolute right-0 top-0 h-full w-20 md:w-50',
              // The strip fades the scrolling links into the nav background.
              { 'bg-fill/alternative-1': fadeStripBackground },
            )}
            style={{
              maskImage:
                'linear-gradient(to left, black 0%, black 20%, transparent 100%)',
              WebkitMaskImage:
                'linear-gradient(to left, black 0%, black 20%, transparent 100%)',
            }}
          />
        )}
      </div>

      {showApplyCta && (
        <div className="shrink-0">
          <StickyNavigationBarApplyButton {...props} label={applyLabel} />
        </div>
      )}
    </div>
  );
};

type NavigationItemProps = INavigationItem & {
  isSelected: boolean;
};

const NavigationItem = memo(
  forwardRef<HTMLDivElement, NavigationItemProps>(
    ({ label, isSelected }, ref) => {
      return (
        <div
          ref={ref}
          className={classNames(
            'flex items-center justify-center text-nowrap shrink-0 mhg-alpha-typography-tag h-[55px] transition-all px-3 snap-center',
            {
              [`border-b-4 ${STICKY_NAV_ACTIVE_BORDER}`]: isSelected,
            },
          )}
        >
          <span
            className={classNames({
              [STICKY_NAV_ACTIVE_TEXT]: isSelected,
            })}
          >
            {label}
          </span>
        </div>
      );
    },
  ),
);

NavigationItem.displayName = 'NavigationItem';
